/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { makeStyles, RACSearchDropdown } from '@rentacenter/racstrap';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { getStoreDetailByNumber } from '../../api/store';
import { GenericObject } from '../../constants/constants';
import { createStoreOption, getStoreByNumber, Store } from '../../domain/Store';
import { SelectFieldOption, StoreDetailResponse } from '../../types/types';

export interface LocalSourceAllocationItemProps {
  readonly index: number;
  readonly allocationField: any;
  readonly onRemoveButtonClick: (index: number) => void;
  readonly disabled: boolean;
  readonly stores: Store[];
  readonly selectStoreOptions: SelectFieldOption[];
  readonly isSearchable?: boolean;
  readonly isStoreLoading?: boolean;
  readonly children?: React.ReactNode;
  readonly onSearch?: (text: string) => void;
}

const HiddenField = (props: GenericObject) => <input {...props} />;

const useStyles = makeStyles((theme: any) => ({
  row: {
    display: 'flex',
    gap: theme.typography.pxToRem(32),
    marginTop: theme.typography.pxToRem(8),
  },
  largeField: {
    width: theme.typography.pxToRem(230),
  },

  button: {
    height: theme.typography.pxToRem(43),
  },
}));
export const LocalSourceAllocationItem = (
  props: LocalSourceAllocationItemProps
) => {
  const classes = useStyles();
  const {
    index,
    disabled,
    allocationField,
    stores,
    selectStoreOptions,
    onSearch,
    isStoreLoading,
    children,
  } = props;

  // We are fetching the first 125 (configurable number) of stores, but the PO can be assigned to a store that
  // is not included into the initial fetch.
  // In this case, we are fetching the store details for that specific store
  // (because name is missing and we cannot display it in the dropdown).
  const [fetchedStore, setFetchedStore] = useState<Store>();
  const [isFetchStoreOptionLoading, setIsFetchStoreOptionLoading] =
    useState(false);
  const [selectedStore, setSelectedStore] = useState<
    SelectFieldOption | undefined
  >();
  const { setValue, errors, getValues } = useFormContext();

  const watchedStoreNum = useWatch({
    name: `allocations[${index}].storeNum`,
  }) as string | undefined;
  useEffect(() => {
    const newStoreNum = watchedStoreNum;

    if (newStoreNum) {
      const foundStore = getStoreByNumber(
        [...stores, fetchedStore].filter((option) => !!option) as Store[],
        newStoreNum
      );

      if (foundStore) {
        setSelectedStore(createStoreOption(foundStore));
        // Also resolve and set the state if not already set
        const currentState = getValues(`allocations[${index}].state`);
        if (!currentState) {
          setStoreState(newStoreNum);
        }
      } else {
        // Store not in local list — fetch it and set state
        const currentState = getValues(`allocations[${index}].state`);
        if (!currentState) {
          setStoreState(newStoreNum);
        }
      }
    }
  }, [watchedStoreNum, fetchedStore, stores]);

  const setStoreState = useCallback(
    async (storeNum: string) => {
      const store: Store | undefined = getStoreByNumber(
        [...stores, fetchedStore].filter((option) => !!option) as Store[],
        storeNum
      );

      if (store?.state?.abbreviation) {
        setValue(`allocations[${index}].state`, store.state.abbreviation, {
          shouldValidate: true,
          shouldDirty: true,
        });
        return;
      }

      setIsFetchStoreOptionLoading(true);
      getStoreDetailByNumber(storeNum)
        .then((data: StoreDetailResponse) => {
          const [storeDetail] = data?.response || [];
          const state =
            storeDetail?.state?.abbreviation ||
            storeDetail?.state ||
            storeDetail?.stateCode;

          if (state) {
            setValue(`allocations[${index}].state`, state, {
              shouldValidate: true,
              shouldDirty: true,
            });
            setFetchedStore({
              ...storeDetail,
              storeNum: storeDetail?.storeNumber,
              state:
                typeof storeDetail?.state === 'string'
                  ? { abbreviation: storeDetail.state }
                  : storeDetail?.state,
            } as any);
          }
        })
        .catch(() => setFetchedStore(undefined))
        .finally(() => setIsFetchStoreOptionLoading(false));
    },
    [fetchedStore, index, stores, setValue]
  );

  const setStoreNumber = useCallback(
    (storeNum: string) => {
      setValue(`allocations[${index}].storeNum`, storeNum, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [index, setValue]
  );

  const onStoreChange = (event: any) => {
    const { value } = event || {};
    if (!value) {
      // eslint-disable-next-line no-console
      console.warn('Local Source store selection missing value', { event });
      return;
    }
    setSelectedStore(event);
    setStoreState(value);
    setStoreNumber(value);
  };

  useEffect(() => {
    if (!selectStoreOptions?.length || !allocationField?.storeNum) return;

    const storeFromOptions = selectStoreOptions.findIndex(
      (option) => option.value === allocationField.storeNum
    );
    if (storeFromOptions < 0) {
      setIsFetchStoreOptionLoading(true);
      getStoreDetailByNumber(allocationField.storeNum)
        .then((data: StoreDetailResponse) => {
          if (data?.response?.length) {
            setFetchedStore({
              ...data.response[0],
              storeNum: data.response[0]?.storeNumber,
            } as any);
          }
        })
        .catch(() => setFetchedStore(undefined))
        .finally(() => setIsFetchStoreOptionLoading(false));
    }
  }, []);

  return (
    <div className={classes.row}>
      <span className={classes.largeField}>
        <Controller
          as={HiddenField}
          type="hidden"
          name={`allocations[${index}].storeNum`}
          rules={{ required: true }}
        />
        <Controller
          as={HiddenField}
          type="hidden"
          name={`allocations[${index}].draftPoDetailsId`}
          rules={{ required: false }}
        />
        <RACSearchDropdown
          name={`allocations[${index}].storeNum`}
          // isLoading and onInputChange are passed to react-select
          //@ts-ignore
          isLoading={isStoreLoading || isFetchStoreOptionLoading}
          //@ts-ignore
          onInputChange={onSearch}
          disabled={disabled}
          inputLabel="Store"
          options={selectStoreOptions}
          OnChange={onStoreChange}
          required
          value={selectedStore}
          errormessage={errors?.allocations?.[index]?.storeNum?.message || ''}
        />
        <Controller
          type="hidden"
          name={`allocations[${index}].state`}
          defaultValue={allocationField.state}
          rules={{ required: true }}
          render={() => <span />}
        />
      </span>
      {children}
    </div>
  );
};
